import React from 'react';
import { formatCurrency, formatDate } from '../../utils/formatters';
import { Transaction } from '../../types';

interface RecentTransactionsProps {
  transactions: Transaction[];
  loading?: boolean;
}

export const RecentTransactions: React.FC<RecentTransactionsProps> = ({
  transactions,
  loading = false
}) => {
  if (loading) {
    return (
      <div style={{ background: '#ffffff', border: '1px solid #e3dcc4', borderRadius: 12, padding: 24, boxShadow: '0 1px 3px rgba(0,0,0,0.06)' }}>
        <div style={{ fontFamily: "'Inter', sans-serif", fontSize: 14, fontWeight: 700, color: '#1c2033', marginBottom: 20 }}>
          Recent Transactions
        </div>
        {[1, 2, 3, 4, 5].map(i => (
          <div key={i} style={{ display: 'flex', alignItems: 'center', gap: 14, marginBottom: 16 }}>
            <div style={{ width: 36, height: 36, borderRadius: 8, background: '#f4efe2', flexShrink: 0 }} />
            <div style={{ flex: 1 }}>
              <div style={{ height: 10, background: '#e3dcc4', borderRadius: 4, marginBottom: 6, width: '65%' }} />
              <div style={{ height: 8, background: '#f4efe2', borderRadius: 4, width: '40%' }} />
            </div>
            <div style={{ height: 10, background: '#e3dcc4', borderRadius: 4, width: 72 }} />
          </div>
        ))}
      </div>
    );
  }

  return (
    <div style={{ background: '#ffffff', border: '1px solid #e3dcc4', borderRadius: 12, padding: 24, boxShadow: '0 1px 3px rgba(0,0,0,0.06)' }}>
      {/* Header */}
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 16 }}>
        <div style={{ fontFamily: "'Inter', sans-serif", fontSize: 14, fontWeight: 700, color: '#1c2033' }}>
          Recent Transactions
        </div>
        <button style={{
          fontFamily: "'Inter', sans-serif", fontSize: 13, fontWeight: 600,
          color: '#d97706', background: 'none', border: 'none', cursor: 'pointer',
        }}>
          View all →
        </button>
      </div>

      {/* Divider */}
      <div style={{ height: 1, background: '#f4efe2', marginBottom: 12 }} />

      {transactions.length === 0 ? (
        <div style={{ textAlign: 'center', padding: '40px 0' }}>
          <svg width="40" height="40" fill="none" stroke="#d9d0b0" viewBox="0 0 24 24" style={{ margin: '0 auto 12px' }}>
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
          </svg>
          <div style={{ fontFamily: "'Inter', sans-serif", fontSize: 13, color: '#8a8778' }}>
            No transactions yet
          </div>
        </div>
      ) : (
        <div>
          {transactions.map((transaction, idx) => {
            const isIncome = transaction.type === 'INCOME';
            return (
              <div
                key={transaction.id}
                style={{
                  display: 'flex', alignItems: 'center', gap: 14,
                  padding: '10px 8px', borderRadius: 8,
                  borderBottom: idx < transactions.length - 1 ? '1px solid #f4efe2' : 'none',
                  transition: 'background 0.15s',
                  cursor: 'default',
                }}
                onMouseEnter={e => { (e.currentTarget as HTMLDivElement).style.background = '#faf7ee'; }}
                onMouseLeave={e => { (e.currentTarget as HTMLDivElement).style.background = 'transparent'; }}
              >
                {/* Type indicator */}
                <div style={{
                  width: 34, height: 34, borderRadius: 8, flexShrink: 0,
                  display: 'flex', alignItems: 'center', justifyContent: 'center',
                  background: isIncome ? 'rgba(16,185,129,0.1)' : 'rgba(239,68,68,0.09)',
                  border: `1px solid ${isIncome ? 'rgba(16,185,129,0.2)' : 'rgba(239,68,68,0.18)'}`,
                  color: isIncome ? '#10b981' : '#ef4444',
                }}>
                  {isIncome ? (
                    <svg width="14" height="14" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M7 11l5-5m0 0l5 5m-5-5v12" />
                    </svg>
                  ) : (
                    <svg width="14" height="14" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 13l-5 5m0 0l-5-5m5 5V6" />
                    </svg>
                  )}
                </div>

                {/* Details */}
                <div style={{ flex: 1, minWidth: 0 }}>
                  <div style={{
                    fontFamily: "'Inter', sans-serif", fontSize: 13, fontWeight: 600,
                    color: '#1c2033', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis',
                  }}>
                    {transaction.description}
                  </div>
                  <div style={{ fontFamily: "'Inter', sans-serif", fontSize: 11, color: '#8a8778', marginTop: 2 }}>
                    {formatDate(transaction.date)} &bull; {transaction.category}
                  </div>
                </div>

                {/* Amount */}
                <div style={{
                  fontFamily: "'Inter', sans-serif",
                  fontSize: 14, fontWeight: 700,
                  color: isIncome ? '#10b981' : '#ef4444',
                  flexShrink: 0,
                }}>
                  {isIncome ? '+' : '-'}{formatCurrency(Math.abs(transaction.amount))}
                </div>
              </div>
            );
          })}
        </div>
      )}

      {transactions.length > 0 && (
        <div style={{ marginTop: 14, paddingTop: 14, borderTop: '1px solid #f4efe2' }}>
          <button style={{
            width: '100%', textAlign: 'center',
            fontFamily: "'Inter', sans-serif", fontSize: 13, fontWeight: 600,
            color: '#8a8778', background: 'none', border: 'none', cursor: 'pointer',
            padding: '4px 0',
          }}
            onMouseEnter={e => { (e.currentTarget as HTMLButtonElement).style.color = '#d97706'; }}
            onMouseLeave={e => { (e.currentTarget as HTMLButtonElement).style.color = '#8a8778'; }}
          >
            View All Transactions
          </button>
        </div>
      )}
    </div>
  );
};
